import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { RaonColDef } from "src/types/grid";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { RemoveRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { useWMS2091_R01Store } from "./Store_WMS2091_R01";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { WMS2091_R01DetailDto } from "./Dto_WMS2091_R01";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useWMS2091_R01Modal } from "./hooks/useWMS2091_R01Modal";
import { useWMS2091_R01CellChange } from "./hooks/WMS2091_R01CellChange";
import { useCodeSearch } from "src/hooks/useCodeSearch";

const COLUMN_STATE_KEY = 'WMS2091_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS2091_R01DetailDto> | null>;
	defaultWMS2091_R01DetailRow: WMS2091_R01DetailDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const DetailTable_WMS2091_R01 = (props: Props) => {

	const { 
		gridRef,
		defaultWMS2091_R01DetailRow,
		clickable, setClickable,
		handleInsertClick,
	} = props;

	const { 
			WMS2091_R01DetailList, setWMS2091_R01DetailList, 
			editWMS2091_R01,
			selectedWMS2091_R01DetailList, setSelectedWMS2091_R01DetailList, removeSelectedWMS2091_R01DetailList, addSelectedWMS2091_R01DetailList, initSelectedWMS2091_R01DetailList,
			deletedWMS2091_R01DetailList, setDeletedWMS2091_R01DetailList, addDeletedWMS2091_R01DetailList, removeDeletedWMS2091_R01DetailList,
			updateWMS2091_R01DetailList, removeWMS2091_R01DetailList,
			originalWMS2091_R01DetailList, setOriginalWMS2091_R01DetailList,
			detailColumnState, setDetailColumnState,
			detailScrollTop, setDetailScrollTop,
		} = useWMS2091_R01Store();
	const { itemStatusList, unitList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useWMS2091_R01Modal();
	const { searchExactCode } = useCodeSearch({});
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS2091_R01DetailList,
		defaultGridData: defaultWMS2091_R01DetailRow,
		selectedRow: selectedWMS2091_R01DetailList,
		originalGridData: originalWMS2091_R01DetailList,
		updateRow: (index: number, data: any) => updateWMS2091_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		unit: '단위',
		itemStatus: '상태',
		qty: '입고량',
	}
	
	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['itemNm', 'itemSpec', 'locationNm', 'unit'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['itemCd', 'itemStatus', 'unit', 'qty'],
		pointerCells: ['remark'],
		insertedPointerCells: ['itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'unit', 'qty'],
		commonPointerCells: [''],
		alignLeftCells: [''],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	
	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<WMS2091_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedWMS2091_R01DetailList,
            deletedList: deletedWMS2091_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		createCellClassRules,
        selectedWMS2091_R01DetailList, deletedWMS2091_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);

	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'unit', 'itemStatus', 'qty', 'remark'];

	const detailTableHandlers = useWMS2091_R01CellChange({
		WMS2091_R01DetailList,
		originalWMS2091_R01DetailList,
		updateWMS2091_R01DetailList,
		gridRef,
		setCodeSearchModalProps,
		customCd: editWMS2091_R01?.customCd,
	});

	const handleGridDropDownCommitAfter = useMemo(() =>
		createGridDropDownCommitAfter<WMS2091_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns]);

	const baseColumnDefs: RaonColDef<WMS2091_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			editWMS2091_R01?.transportNo === '' ?
				(params: WMS2091_R01DetailDto) => renderIndexCell(
					params,
					(innerParams: WMS2091_R01DetailDto) => getRowStyleFlags(
						innerParams,
						{
							deletedList: deletedWMS2091_R01DetailList,
							selectedList: selectedWMS2091_R01DetailList,
							dataList: WMS2091_R01DetailList
						}
					),
					(rowIndex: number, e: React.MouseEvent) => {
						if (e.ctrlKey) {
							for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
						} else {
							handleInsertClick(e, rowIndex);
						}
					},
					(rowData) => RemoveRowHandler(
						gridRef,
						WMS2091_R01DetailList,
						rowData,
						setClickable,
						removeWMS2091_R01DetailList,
						(valueIndex) => addDeletedWMS2091_R01DetailList([valueIndex.toString()])
					),
				)
			: undefined
		),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			editable: ((params: any) => params.data?.inserted === true),
			pasteable: true,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 250,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			editable: false,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			editable: false,
		},
		{
			headerName: '단위', colId: 'unit', field: 'unit', width: 70,
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updateWMS2091_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '상태', colId: 'itemStatus', field: 'itemStatus', width: 60,editable : false,
			...CommonCodeColumn.options(itemStatusList, ((params: any) => params.data?.inserted === true), CommonCodeColumn.change(updateWMS2091_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '입고량', colId: 'qty', field: 'qty', width: 80,
			editable: ((params: any) => params.data?.inserted === true),
			pasteable: true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
	], [deletedWMS2091_R01DetailList, WMS2091_R01DetailList, editWMS2091_R01?.transportNo, handleInsertClick]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<WMS2091_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<WMS2091_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]); 

	
	const handleRowClick = useRowSelectionHandler({
		dataList: WMS2091_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedWMS2091_R01DetailList,
		deletedList: deletedWMS2091_R01DetailList,
		addSelected: addSelectedWMS2091_R01DetailList,
		removeSelected: removeSelectedWMS2091_R01DetailList,
		clickable, setClickable
	});

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 품목 코드 더블클릭으로 품목 검색 모달 열기
	 * - 키다운 네비게이션 (엔터 시 수량 필드로 이동, 방향키 네비게이션)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<WMS2091_R01DetailDto>({
		detailList: WMS2091_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목',
				responseMapping: {
					itemCd: 'itemCd',
					itemNm: 'itemNm',
					itemSpec: 'itemSpec',
					itemUnit: 'unit',
					itemCost: 'cost',
					itemStatus: 'itemStatus',
					locationCd: 'locationCd',
					locationNm: 'locationNm',
				},
				clearFields: {
					itemCd: "",
					itemNm: "",
					itemSpec: "",
					unit: "",
					cost: 0,
					amt: 0,
					itemStatus: "",
					locationCd: "",
					locationNm: "",
				},
				searchFunction: (code: string, onSuccess?: (item: any) => void) =>
					searchExactCode('item', code, onSuccess)
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps,
		},
		constraints: {
			notEdit: (params) => {
				if (params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'qty',
			enableArrowNavigation: true,
		}
	});

	const handlePostSort = useCallback((params: PostSortRowsParams<WMS2091_R01DetailDto>) => {
		postSortHandler(params, WMS2091_R01DetailList, originalWMS2091_R01DetailList, setWMS2091_R01DetailList);
	}, [WMS2091_R01DetailList, originalWMS2091_R01DetailList, setWMS2091_R01DetailList]);

	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = WMS2091_R01DetailList
			.reduce((sum, item) => sum + (deletedWMS2091_R01DetailList.includes(item.itemCd) ? 0 : Number(item.qty) || 0), 0);

		return [{
			itemCd: WMS2091_R01DetailList.length.toLocaleString() + "건",
			qty: totalQty,
		}];	  
	}, [WMS2091_R01DetailList, deletedWMS2091_R01DetailList,]);

	return (
		<GridContextWrapper<WMS2091_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={WMS2091_R01DetailList}
			setPaddedGridData={setWMS2091_R01DetailList}
			initSelected={initSelectedWMS2091_R01DetailList}
			defaultRowData={defaultWMS2091_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<WMS2091_R01DetailDto>
				ref={gridRef}
				rowData={WMS2091_R01DetailList}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}

				disableBuiltinCellSelection={false}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}

				rowClassRules={createRowClassRules<WMS2091_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedWMS2091_R01DetailList,
					deletedList: deletedWMS2091_R01DetailList
				})}

				onRowClicked={handleRowClick}
				onCellClicked={cellEventHandlers.handleCellClick}
				onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={detailTableHandlers.handleCellChange}
				onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}
				pinnedBottomRowData={pinnedBottomRowData}
				onPostSortRows={handlePostSort}
			/>
		</GridContextWrapper>
	)
}

